import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Send, PlugZap, CheckCircle2, AlertCircle, Eye, EyeOff, Reply, Copy, Webhook, Building2, Trash2 } from "lucide-react";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { aiApi } from "./aiApi";
import { setMickeyMood, celebrateMickey } from "./mickeyMood";
import AICustomWaSummary from "./AICustomWaSummary";

/** Sambungan satu nombor WhatsApp (wabot.my) untuk laporan AI Assistant. */
export default function AIWabotSettings({ embedded = false }) {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ base_url: "", instance_id: "", access_token: "", sender_phone: "" });
  const [showTok, setShowTok] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);
  const [registering, setRegistering] = useState(false);
  const toggleManagers = async (v) => {
    try { apply(await aiApi.saveWabotConfig({ inbound_managers: v })); toast.success(v ? "Pengurus boleh tanya Mimi" : "Pengurus tidak lagi boleh tanya Mimi"); }
    catch { toast.error("Gagal menyimpan"); }
  };
  const toggleDelete = async (v) => {
    try { apply(await aiApi.saveWabotConfig({ wa_delete_enabled: v })); toast.success(v ? "Padam/pulih melalui WhatsApp diaktifkan" : "Padam/pulih melalui WhatsApp dimatikan"); }
    catch { toast.error("Gagal menyimpan"); }
  };
  const toggleInbound = async (v) => {
    try { apply(await aiApi.saveWabotConfig({ inbound_enabled: v })); toast.success(v ? "Balas di WhatsApp diaktifkan" : "Balas di WhatsApp dimatikan"); }
    catch { toast.error("Gagal menyimpan"); }
  };
  const copyUrl = async () => {
    try { await navigator.clipboard.writeText(cfg.webhook_url); toast.success("URL webhook disalin"); } catch { toast.error("Tidak dapat menyalin"); }
  };
  const register = async () => {
    setRegistering(true); setMickeyMood("thinking");
    try {
      const r = await aiApi.wabotRegisterWebhook();
      if (r.ok) { toast.success("Webhook didaftarkan di Wabot"); celebrateMickey(); setCfg((c) => ({ ...c, webhook_registered_at: new Date().toISOString() })); }
      else { toast.error(`Gagal: ${r.response}`); setMickeyMood("sad", 5000); }
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal mendaftar webhook"); setMickeyMood("idle"); } finally { setRegistering(false); }
  };

  const apply = (c) => { setCfg(c); setForm({ base_url: c.base_url || "", instance_id: c.instance_id || "", access_token: "", sender_phone: c.sender_phone || "" }); };
  useEffect(() => { aiApi.wabotConfig().then(apply).catch(() => toast.error("Gagal memuat tetapan Wabot")); }, []);

  const save = async () => {
    if (!form.instance_id.trim()) return toast.error("Sila isi Instance ID");
    if (!cfg?.has_token && !form.access_token.trim()) return toast.error("Sila isi Access Token");
    setSaving(true);
    try { apply(await aiApi.saveWabotConfig(form)); toast.success("Sambungan Wabot disimpan"); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); } finally { setSaving(false); }
  };
  const test = async () => {
    if (!testPhone.trim()) return toast.error("Isi nombor untuk ujian");
    setTesting(true); setResult(null); setMickeyMood("thinking");
    try {
      const r = await aiApi.wabotTest(testPhone);
      setResult(r);
      if (r.ok) { toast.success("Mesej ujian dihantar"); celebrateMickey(); } else { toast.error(`Gagal: ${r.response}`); setMickeyMood("sad", 5000); window.dispatchEvent(new Event("ai:refresh-summary")); }
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); setMickeyMood("idle"); } finally { setTesting(false); }
  };

  if (!cfg) return <div className="grid place-items-center h-16"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>;
  if (cfg.provider === "custom" && !embedded) return <AICustomWaSummary cfg={cfg} />;
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-2 mb-2 space-y-2" data-testid="ai-wabot-settings">
      <div className="flex items-center gap-2">
        <PlugZap className="h-4 w-4 text-emerald-600" />
        <div className="flex-1 text-[12px] font-bold text-slate-700">Sambungan Nombor WhatsApp (Wabot)</div>
        {cfg.configured
          ? <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-700" data-testid="ai-wabot-status"><CheckCircle2 className="h-3.5 w-3.5" />Disambung</span>
          : <span className="flex items-center gap-1 text-[10px] font-bold text-rose-600" data-testid="ai-wabot-status"><AlertCircle className="h-3.5 w-3.5" />Belum disambung</span>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="text-[10px] text-slate-500 col-span-2">URL Dashboard Wabot
          <Input value={form.base_url} onChange={(e) => setForm({ ...form, base_url: e.target.value })} placeholder="https://app.wabot.my" className="h-8 text-xs bg-white mt-0.5" data-testid="ai-wabot-url" />
        </label>
        <label className="text-[10px] text-slate-500">Instance ID
          <Input value={form.instance_id} onChange={(e) => setForm({ ...form, instance_id: e.target.value })} placeholder="cth 6620ABC12345" className="h-8 text-xs bg-white mt-0.5" data-testid="ai-wabot-instance" />
        </label>
        <label className="text-[10px] text-slate-500">Nombor penghantar (label)
          <Input value={form.sender_phone} onChange={(e) => setForm({ ...form, sender_phone: e.target.value })} placeholder="cth 0123456789" inputMode="tel" className="h-8 text-xs bg-white mt-0.5" data-testid="ai-wabot-sender" />
        </label>
        <label className="text-[10px] text-slate-500 col-span-2">Access Token {cfg.has_token && <span className="text-slate-400">(semasa: {cfg.access_token_masked} — kosongkan untuk kekal)</span>}
          <div className="relative mt-0.5">
            <Input type={showTok ? "text" : "password"} value={form.access_token} onChange={(e) => setForm({ ...form, access_token: e.target.value })}
              placeholder={cfg.has_token ? "••••••••" : "Access token dari dashboard Wabot"} className="h-8 text-xs bg-white pr-8" data-testid="ai-wabot-token" />
            <button type="button" onClick={() => setShowTok((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" aria-label="Tunjuk token">
              {showTok ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </button>
          </div>
        </label>
      </div>
      <div className="flex justify-end">
        <Button size="sm" onClick={save} disabled={saving} className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" data-testid="ai-wabot-save">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}Simpan
        </Button>
      </div>
      {cfg.configured && (
        <div className="flex items-center gap-2 pt-1 border-t border-slate-200">
          <Input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="No. untuk uji sambungan" inputMode="tel" className="h-7 text-xs bg-white" data-testid="ai-wabot-test-phone" />
          <Button size="sm" variant="outline" onClick={test} disabled={testing} className="h-7 text-xs shrink-0" data-testid="ai-wabot-test-btn">
            {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Send className="h-3.5 w-3.5 mr-1" />}Uji
          </Button>
        </div>
      )}
      {result && <div className={`text-[11px] ${result.ok ? "text-emerald-700" : "text-rose-600"}`} data-testid="ai-wabot-test-result">{result.ok ? "Berjaya" : "Gagal"}: {result.response}</div>}

      <div className="pt-2 border-t border-slate-200 space-y-1.5" data-testid="ai-wa-inbound">
        <div className="flex items-center gap-2">
          <Reply className="h-4 w-4 text-fuchsia-500" />
          <div className="flex-1 text-[12px] font-bold text-slate-700">Balas di WhatsApp — tanya Mimi Junior</div>
          <Switch checked={cfg.inbound_enabled !== false} onCheckedChange={toggleInbound} data-testid="ai-wa-inbound-switch" />
        </div>
        <div className="text-[11px] text-slate-500 leading-snug">
          Penerima di bawah boleh <b>reply</b> mesej laporan atau hantar soalan terus ke nombor Wabot — Mimi Junior akan menjawab.
          Superadmin juga boleh hantar <b>gambar / PDF</b> untuk dibaca & diringkaskan. Arahan: <b>BAHARU</b>, <b>SAHKAN</b>, <b>BATAL</b>, <b>BANTUAN</b>.
        </div>
        <div className={`flex items-center gap-2 rounded-lg px-2 py-1 ${cfg.inbound_enabled !== false ? "bg-indigo-50" : "bg-slate-50 opacity-60"}`}>
          <Building2 className="h-3.5 w-3.5 text-indigo-500" />
          <div className="flex-1 text-[11px] text-indigo-800">Pengurus cawangan boleh tanya Mimi <b>(data cawangan sendiri sahaja)</b></div>
          <Switch checked={cfg.inbound_managers !== false} disabled={cfg.inbound_enabled === false} onCheckedChange={toggleManagers} data-testid="ai-wa-inbound-managers" />
        </div>
        <div className={`flex items-center gap-2 rounded-lg px-2 py-1 ${cfg.inbound_enabled !== false ? "bg-rose-50" : "bg-slate-50 opacity-60"}`}>
          <Trash2 className="h-3.5 w-3.5 text-rose-500" />
          <div className="flex-1 text-[11px] text-rose-800">Superadmin boleh <b>padam / pulih data</b> melalui WhatsApp (balas YA, maks 50 rekod, tiada lampiran/pautan)</div>
          <Switch checked={cfg.wa_delete_enabled !== false} disabled={cfg.inbound_enabled === false} onCheckedChange={toggleDelete} data-testid="ai-wa-delete-switch" />
        </div>
        <div className="flex items-center gap-1">
          <Webhook className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          <div className="flex-1 min-w-0 h-7 rounded-md border border-slate-200 bg-white px-2 text-[10.5px] leading-7 font-mono text-slate-600 truncate select-all" title={cfg.webhook_url} data-testid="ai-wa-webhook-url">{cfg.webhook_url}</div>
          <Button size="sm" variant="outline" className="h-7 w-8 p-0 shrink-0" onClick={copyUrl} aria-label="Salin URL" data-testid="ai-wa-webhook-copy"><Copy className="h-3.5 w-3.5" /></Button>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={register} disabled={registering || !cfg.configured} className="h-7 text-xs border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50" data-testid="ai-wa-webhook-register">
            {registering ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Webhook className="h-3.5 w-3.5 mr-1" />}Daftar webhook di Wabot
          </Button>
          <span className="text-[10px] text-slate-400" data-testid="ai-wa-webhook-status">
            {cfg.webhook_registered_at ? `Didaftar ${new Date(cfg.webhook_registered_at).toLocaleString("ms-MY", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}` : "Atau tampal URL ini di dashboard Wabot → Webhook"}
          </span>
        </div>
      </div>
    </div>
  );
}
